import { Alert, Box, Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { useColorMode } from '@src/components/chakra-ui/color-mode'
import { tokens } from '@src/theme/tokens'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { FormEvent, useEffect, useMemo, useState } from 'react'

type SessionPrechargePaymentProps = {
  clientSecret: string
  /**
   * Stripe Customer Session for Chargebee-managed cards on the platform account.
   * Omit for Standard Connect direct charges (different Stripe account).
   */
  customerSessionClientSecret?: string | null
  /** Required for Standard Connect direct charges. */
  stripeConnectAccountId?: string | null
  amountUsd: number
  onAuthorized: () => void | Promise<void>
  onCancel: () => void | Promise<void>
}

const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHED_KEY

const SessionPrechargePaymentInner = (props: SessionPrechargePaymentProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [canceling, setCanceling] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setErrorMessage(null)

    if (!stripe || !elements) {
      setErrorMessage('Payment form is still loading. Try again in a moment.')
      return
    }

    setProcessing(true)
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // Manual capture PIs finish without redirect in normal card flows.
          return_url: typeof window !== 'undefined' ? window.location.href : 'https://www.startups.com',
        },
        redirect: 'if_required',
      })

      if (result.error) {
        setErrorMessage(result.error.message || 'Card authorization failed.')
        setProcessing(false)
        return
      }

      const status = result.paymentIntent?.status
      if (status === 'requires_capture' || status === 'succeeded' || status === 'processing') {
        await props.onAuthorized()
        return
      }

      setErrorMessage(`Unexpected payment status: ${status || 'unknown'}. Try another card.`)
      setProcessing(false)
    } catch (err: any) {
      setErrorMessage(err?.message || 'Could not authorize payment.')
      setProcessing(false)
    }
  }

  const handleCancel = async () => {
    setCanceling(true)
    try {
      await props.onCancel()
    } finally {
      setCanceling(false)
    }
  }

  return (
    <VStack align={'stretch'} as={'form'} gap={4} onSubmit={handleSubmit}>
      <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
        Hold{' '}
        <Text as={'span'} color={'colorPalette.fg'} fontWeight={'semibold'}>
          ${Number(props.amountUsd).toFixed(2)}
        </Text>{' '}
        on a card. You’re charged this full package when the advisor confirms a time. Cancel before
        anyone joins for a full refund.
        {props.customerSessionClientSecret
          ? ' Cards already on your membership billing are listed here. A new card is used for this session only — it is not saved to membership billing.'
          : null}
      </Text>

      {errorMessage && (
        <Alert.Root borderRadius={'md'} status={'error'}>
          <Alert.Indicator />
          <Box ml={2}>{errorMessage}</Box>
        </Alert.Root>
      )}

      <PaymentElement
        options={{
          layout: 'auto',
          paymentMethodOrder: ['card'],
        }}
      />

      <HStack gap={3} wrap={'wrap'}>
        <Button disabled={!stripe || canceling} loading={processing} type={'submit'} variant={'cta'}>
          Authorize card
        </Button>
        <Button
          disabled={processing}
          loading={canceling}
          type={'button'}
          variant={'outline'}
          onClick={() => void handleCancel()}
        >
          Cancel request
        </Button>
      </HStack>
    </VStack>
  )
}

/**
 * Stripe Elements step after requestAdvisorSession returns a precharge client secret.
 * Standard Connect direct charges need stripeConnectAccountId on loadStripe.
 */
export const SessionPrechargePayment = (props: SessionPrechargePaymentProps) => {
  const { colorMode } = useColorMode()
  const isDarkMode = colorMode === 'dark'
  const colors = tokens.tokens.colors

  const stripePromise = useMemo(() => {
    if (!publishableKey) {
      return null
    }
    const accountId = props.stripeConnectAccountId?.trim()
    if (accountId) {
      return loadStripe(publishableKey, { stripeAccount: accountId })
    }
    return loadStripe(publishableKey)
  }, [props.stripeConnectAccountId])

  const [stripe, setStripe] = useState<Stripe | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!stripePromise) {
      setStripe(null)
      return
    }
    void stripePromise.then(s => {
      if (!cancelled) {
        setStripe(s)
      }
    })
    return () => {
      cancelled = true
    }
  }, [stripePromise])

  if (!publishableKey) {
    return (
      <Alert.Root borderRadius={'md'} status={'error'}>
        <Alert.Indicator />
        <Box ml={2}>Stripe is not configured on this front-end (NEXT_PUBLIC_STRIPE_PUBLISHED_KEY).</Box>
      </Alert.Root>
    )
  }

  if (!stripe || !props.clientSecret) {
    return (
      <HStack gap={3} justify={'center'} py={6}>
        <Spinner size={'sm'} />
        <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
          Loading payment form…
        </Text>
      </HStack>
    )
  }

  return (
    <Elements
      stripe={stripe}
      options={{
        clientSecret: props.clientSecret,
        ...(props.customerSessionClientSecret
          ? { customerSessionClientSecret: props.customerSessionClientSecret }
          : {}),
        appearance: {
          theme: 'stripe',
          variables: {
            borderRadius: '6px',
            colorBackground: isDarkMode ? colors.purple['900'].value : colors.orange['card'],
            colorDanger: colors.red['500'].value,
            colorPrimary: isDarkMode ? colors.purple['300'].value : colors.orange['600'].value,
            colorSuccess: colors.green['500'].value,
            colorText: isDarkMode ? colors.gray['100'].value : colors.gray['900'].value,
            colorTextPlaceholder: isDarkMode ? colors.gray['200'].value : colors.gray['900'].value,
            fontFamily: 'Lato, system-ui, sans-serif',
            spacingUnit: '4px',
            gridRowSpacing: '16px',
            fontSizeBase: '16px',
          },
        },
      }}
    >
      <SessionPrechargePaymentInner {...props} />
    </Elements>
  )
}
